import { COMMA, ENTER, TAB } from '@angular/cdk/keycodes';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, startWith, switchMap } from 'rxjs/operators';
import { InternalTag } from './model';
import { TAG_ALLOWED_CHARS, validateToken } from './tag-parse.util';

@UntilDestroy()
@Component({
  selector: 'miq-view-tags',
  templateUrl: 'view-tags.component.html',
  styleUrls: ['view-tags.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ViewTagsComponent implements OnInit, OnDestroy {
  @Input() tags: InternalTag[];
  @Input() filterTags: (filter: string) => Observable<InternalTag[]>;
  @Input() canEdit = false;
  @Input() minFilterChars = 2;
  @Input() enableClipboardFeatures = false;
  @Input() entityId?: number | string;
  @Input() addTagWhenEnter = false;
  @Output() tagAdded = new EventEmitter<InternalTag>();
  @Output() tagRemoved = new EventEmitter<InternalTag>();
  @Output() clipboardCut = new EventEmitter<{ tagNames: string[] }>();
  @Output() clipboardPaste = new EventEmitter<{ clipboardTags: string[] }>();
  @Output() chipClick = new EventEmitter<{ tag: InternalTag; event: Event }>();

  @ViewChild('tagsInput') tagsInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;

  public tagCtrl = new UntypedFormControl();
  public separatorKeysCodes: number[] = [ENTER, COMMA, TAB];
  public filteredTags: Observable<InternalTag[]>;
  public allowedChars = TAG_ALLOWED_CHARS;
  public selectedTagIds = new Set<number>();
  public isClipboardSupported = false;

  constructor() {}

  ngOnInit() {
    this.filteredTags = this.tagCtrl.valueChanges.pipe(
      untilDestroyed(this),
      // null as string to avoid deprecated signature startWith<T>(scheduler: SchedulerLike)
      startWith(null as string),
      debounceTime(300),
      switchMap((tag: string | null) => {
        const validFilterableTag = tag && tag.trim().length > this.minFilterChars;
        if (validFilterableTag) {
          return this.filterTags(tag.trim());
        } else {
          return of([]);
        }
      }),
      catchError(() => of([]))
    );
  }

  ngOnDestroy() {
    // Required by until destroyed
    this.tagsInput = null;
    this.matAutocomplete = null;
  }

  /**
   * Emit chip click event to parent for handling
   */
  public onChipClick(tag: InternalTag, event: Event): void {
    if (!this.canEdit || !this.enableClipboardFeatures) return;
    if (this.enableClipboardFeatures) this.isClipboardSupported = true;
    event.stopPropagation();

    // Emit event to parent for selection handling
    this.chipClick.emit({ tag, event });
  }

  /**
   * Check if a tag is selected
   */
  public isTagSelected(tag: InternalTag): boolean {
    return this.enableClipboardFeatures && this.selectedTagIds.has(tag.internalId);
  }

  /**
   * Handle clipboard cut event from directive - emit to parent for processing
   */
  public onClipboardCut(event: { tagNames: string[] }): void {
    this.clipboardCut.emit(event);
  }

  /**
   * Handle clipboard paste event from directive - emit to parent for processing
   */
  public onClipboardPaste(event: { clipboardTags: string[] }): void {
    this.clipboardPaste.emit(event);
  }

  public remove(tag: InternalTag): void {
    if (this.canEdit) {
      this.tagRemoved.emit(tag);
    }
  }

  public selected(event: MatAutocompleteSelectedEvent): void {
    this.tagAdded.emit(event.option.value);
    this.tagsInput.nativeElement.value = '';
    this.tagCtrl.setValue(null);
  }

  public onPaste(event: ClipboardEvent) {
    const data = event.clipboardData.getData('text');
    const quotedRegex = /"([^"]*)"/g;
    let match;
    const cleanCharRegex = new RegExp('[a-zA-Z0-9' + TAG_ALLOWED_CHARS + ']+', 'g');
    const tags: string[] = [];
    let hasQuotedValues = false;

    // First, try to parse quoted CSV values: "value1","value2"
    while ((match = quotedRegex.exec(data)) !== null) {
      hasQuotedValues = true;
      const quotedContent = match[1]; // Content inside quotes
      // Extract and join all valid characters (preserves commas inside quotes)
      const validMatches = quotedContent.match(cleanCharRegex) || [];
      const cleanTag = validMatches.join('').trim();
      if (cleanTag.length > 0) {
        tags.push(cleanTag);
      }
    }

    // Create set of existing tag names from this.tags
    const existingTagNames = new Set(this.tags.map(tag => tag.name));

    // If we found quoted values, emit each as a separate tag (filtered to exclude existing)
    if (hasQuotedValues && tags.length > 0) {
      // Filter out tags that already exist in this.tags
      const newTags = tags.filter(tagName => !existingTagNames.has(tagName));

      // Emit each new tag for addition (tag names only, no IDs)
      newTags.forEach(tag => {
        this.tagAdded.emit({ display: tag, name: tag } as InternalTag);
      });
    } else {
      // No quoted values: treat entire input as a single tag
      // Extract all valid characters (removes special chars like /)
      let finalString = '';
      let tempData = data;
      let result;
      while ((result = cleanCharRegex.exec(tempData)) !== null) {
        finalString += result[0];
      }

      // Only set if the tag doesn't already exist
      if (finalString.trim().length > 0 && !existingTagNames.has(finalString.trim())) {
        this.tagCtrl.setValue(finalString.trim());
      }
    }

    this.tagsInput.nativeElement.value = '';
    event.preventDefault();
  }

  /**
   * Handles when the user clicks on enter this is suppose to handle the enter as only one tag
   * @param evt
   * @returns
   */
  public onClickEnter(evt: KeyboardEvent) {
    if (!this.addTagWhenEnter) return;
    // Keep the popup open after adding the tag
    evt.preventDefault();
    evt.stopPropagation();

    const target = evt?.target as HTMLInputElement;
    // Validate if the input has only valid characters
    const inputValue = target?.value?.trim() ?? '';
    // The input already prevents the user from typing any invalid character
    // keep the validation as a safe guard
    if (!validateToken(inputValue)) return;
    this.tagAdded.emit({ display: inputValue, name: inputValue } as InternalTag);
    // Clean up the Input and hide the auto-complete options
    this.matAutocomplete.showPanel = false;
    this.tagsInput.nativeElement.value = '';
    this.tagCtrl.setValue(null);
  }
}
